import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

import config from '@/config/environment';

// Types
export type ThemeMode = 'light' | 'dark' | 'system';
export type ColorScheme = 'light' | 'dark';

export interface ThemePreferences {
  mode: ThemeMode;
  primaryColor: string;
  fontSize: 'small' | 'medium' | 'large';
  reducedMotion: boolean;
  highContrast: boolean;
}

export interface ThemeContextType {
  // Current theme state
  mode: ThemeMode;
  colorScheme: ColorScheme;
  preferences: ThemePreferences;

  // Theme actions
  setMode: (mode: ThemeMode) => void;
  setPrimaryColor: (color: string) => void;
  setFontSize: (size: 'small' | 'medium' | 'large') => void;
  setReducedMotion: (enabled: boolean) => void;
  setHighContrast: (enabled: boolean) => void;
  resetPreferences: () => void;

  // Utility functions
  isDark: boolean;
  isLight: boolean;
  toggleMode: () => void;
}

// Default theme preferences
const defaultPreferences: ThemePreferences = {
  mode: 'system',
  primaryColor: '#047401',
  fontSize: 'medium',
  reducedMotion: false,
  highContrast: false,
};

// Create the context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Custom hook to use the theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// ThemeProvider component
interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const darkModeEnabled = config.features.darkMode;

  const [preferences, setPreferences] =
    useState<ThemePreferences>(defaultPreferences);
  const [colorScheme, setColorScheme] = useState<ColorScheme>('light');

  // Load preferences from localStorage on mount
  useEffect(() => {
    const savedPreferences = localStorage.getItem('theme-preferences');
    if (savedPreferences) {
      try {
        const parsed = JSON.parse(savedPreferences);
        const merged = { ...defaultPreferences, ...parsed } as ThemePreferences;
        // If dark mode is disabled, force light mode
        if (!darkModeEnabled) {
          merged.mode = 'light';
        }
        setPreferences(merged);
      } catch (error) {}
    } else if (!darkModeEnabled) {
      // No saved prefs and dark mode disabled: ensure light mode
      setPreferences(prev => ({ ...prev, mode: 'light' }));
    }
  }, [darkModeEnabled]);

  // Save preferences to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('theme-preferences', JSON.stringify(preferences));
  }, [preferences]);

  // Determine color scheme based on mode and system preference
  useEffect(() => {
    const updateColorScheme = () => {
      // If dark mode feature is disabled, always use light
      if (!darkModeEnabled) {
        setColorScheme('light');
        return;
      }

      if (preferences.mode === 'system') {
        const systemPrefersDark = window.matchMedia(
          '(prefers-color-scheme: dark)'
        ).matches;
        setColorScheme(systemPrefersDark ? 'dark' : 'light');
      } else {
        setColorScheme(preferences.mode);
      }
    };

    updateColorScheme();

    // Listen for system theme changes (only when feature enabled)
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (preferences.mode === 'system') {
        updateColorScheme();
      }
    };

    if (darkModeEnabled) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    return () => {};
  }, [preferences.mode, darkModeEnabled]);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;

    // Apply color scheme
    root.classList.toggle('dark', colorScheme === 'dark');
    root.setAttribute('data-theme', colorScheme);

    // Apply primary color
    root.style.setProperty('--primary-color', preferences.primaryColor);

    // Apply font size
    const fontSizeMap = {
      small: '14px',
      medium: '16px',
      large: '18px',
    };
    root.style.setProperty(
      '--base-font-size',
      fontSizeMap[preferences.fontSize]
    );

    // Apply reduced motion
    if (preferences.reducedMotion) {
      root.style.setProperty('--motion-reduce', 'reduce');
    } else {
      root.style.removeProperty('--motion-reduce');
    }

    // Apply high contrast
    root.classList.toggle('high-contrast', preferences.highContrast);

    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute(
        'content',
        colorScheme === 'dark' ? '#000000' : '#ffffff'
      );
    }
  }, [colorScheme, preferences]);

  // Theme actions
  const setMode = (mode: ThemeMode) => {
    if (!darkModeEnabled) {
      // Ignore external attempts to set dark/system when feature is disabled
      setPreferences(prev => ({ ...prev, mode: 'light' }));
      return;
    }
    setPreferences(prev => ({ ...prev, mode }));
  };

  const setPrimaryColor = (primaryColor: string) => {
    setPreferences(prev => ({ ...prev, primaryColor }));
  };

  const setFontSize = (fontSize: 'small' | 'medium' | 'large') => {
    setPreferences(prev => ({ ...prev, fontSize }));
  };

  const setReducedMotion = (reducedMotion: boolean) => {
    setPreferences(prev => ({ ...prev, reducedMotion }));
  };

  const setHighContrast = (highContrast: boolean) => {
    setPreferences(prev => ({ ...prev, highContrast }));
  };

  const resetPreferences = () => {
    setPreferences(defaultPreferences);
  };

  const toggleMode = () => {
    if (!darkModeEnabled) return;
    if (preferences.mode === 'light') {
      setMode('dark');
    } else if (preferences.mode === 'dark') {
      setMode('system');
    } else {
      setMode('light');
    }
  };

  // Computed values
  const isDark = colorScheme === 'dark';
  const isLight = colorScheme === 'light';

  // Context value
  const value: ThemeContextType = {
    // Current theme state
    mode: preferences.mode,
    colorScheme,
    preferences,

    // Theme actions
    setMode,
    setPrimaryColor,
    setFontSize,
    setReducedMotion,
    setHighContrast,
    resetPreferences,

    // Utility functions
    isDark,
    isLight,
    toggleMode,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export default ThemeProvider;
